import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, RadioGroup, RadioTile } from '@eneco/sparky';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import type { GenericChoiceStepContent } from './generic-choice-step-content.types';
import { Header, Layout, NextButton } from '../../components';
import { useStepContent, useStepKey } from '../../hooks';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import { getIllustration } from '../../utils/getIllustration';

interface FormFields {
  [key: string]: string;
}
// TODO: Remove this component and it's related files once we replaced all with the GenericRadioChoiceStep or GenericCheckboxChoiceStep
export const GenericChoiceStep = () => {
  // This step won't re-render properly when used on multiple consecutive steps
  // without a key that changes per step page. See `useStepKey` for why the
  // rendering's `uid` is not that key.
  return <GenericChoice key={useStepKey()} />;
};

const GenericChoice: FC = () => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);
  const textualData = useStepContent<GenericChoiceStepContent>('GenericChoiceStep');

  const genericFieldName = textualData?.choice?.name ?? '';

  const formSchema = yup.object({
    [genericFieldName]: yup.string().required(textualData?.choice?.genericContentRadio?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [genericFieldName]: flowContext[genericFieldName as keyof FlowContext]?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const mutatedGenericField = watch(genericFieldName);

  const showNonBlockingNotification =
    mutatedGenericField === textualData?.choice?.nonBlockingValue &&
    textualData?.content?.nonBlockingChoiceNotification?.content;
  const showBlockingNotification =
    mutatedGenericField === textualData?.choice?.blockingValue &&
    textualData?.genericError?.errorBlockingChoiceNotification?.content;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      let genericFieldValue: string | boolean | number = formData[genericFieldName];

      // convert genericFieldValue to appropriate type
      if (genericFieldValue.toLowerCase() === 'true' || genericFieldValue.toLowerCase() === 'false') {
        genericFieldValue = genericFieldValue.toLowerCase() === 'true';
      } else if (Number(genericFieldValue) || Number(genericFieldValue) === 0) {
        genericFieldValue = Number(genericFieldValue);
      }

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          [genericFieldName]: genericFieldValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title}</Header.Title>
        {textualData?.content?.content && <RichText html={textualData.content.content} />}
      </Header>

      {textualData?.content?.notification?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.variant}
          title={textualData?.content?.notification?.title}
          text={<RichText html={textualData?.content?.notification?.content} />}
        />
      )}

      <Controller
        control={control}
        name={genericFieldName}
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby={genericFieldName}
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors?.[genericFieldName]?.message}>
            {textualData?.choice?.genericContentRadio?.options?.map(({ name, label, illustration }) => {
              const Illustration = getIllustration(illustration);

              return (
                <RadioTile
                  key={name}
                  value={name ?? ''}
                  icon={Illustration && <Illustration size="small" color="currentColor" />}>
                  {label ?? ''}
                </RadioTile>
              );
            })}
          </RadioGroup>
        )}
      />

      {showNonBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.nonBlockingChoiceNotification?.variant}
          title={textualData?.content?.nonBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.content?.nonBlockingChoiceNotification?.content} />}
        />
      )}

      {!showBlockingNotification && (
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText}</NextButton>
      )}

      {showBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.genericError?.errorBlockingChoiceNotification?.variant}
          title={textualData?.genericError?.errorBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.genericError?.errorBlockingChoiceNotification?.content} />}
        />
      )}
    </Layout.Content>
  );
};
